import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject, selectSaveId } from '@/store/slices/project/selectors';

/**
 * Custom hook to handle actions after a save operation is completed when there is a specific save ID.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.saveId - The ID of the save operation to track.
 * @param {function} params.onComplete - Callback function to execute when the save operation is complete.
 */
const useOnFinishSave = ({
  saveId,
  onComplete,
  onStart,
}: {
  saveId: string;
  onComplete?: () => void;
  onStart?: () => void;
}): { saving: boolean } => {
  const storeSaveId = useAppSelector(selectSaveId);
  const savingProject = useAppSelector(selectIsSavingWorkingProject);

  const [savingState, setSavingState] = useState(false);

  const onStartTriggered = useRef(false);

  // if saveId matches the store's saveId, set savingState to true to track that the save operation is in progress
  useEffect(() => {
    if (saveId === storeSaveId) {
      setSavingState(true);
      if (!onStartTriggered.current) {
        onStart?.();
      }
      onStartTriggered.current = true;
    }
  }, [onStart, saveId, storeSaveId]);

  // if savingState is true and the store's saving state changes to false,
  // we know that the save operation has completed and we can call the onComplete callback
  useEffect(() => {
    if (savingState && !savingProject) {
      onComplete?.();
      setSavingState(false); // Reset saving state after completion
      onStartTriggered.current = false; // Reset the onStart trigger
    }
  }, [onComplete, savingProject, savingState]);

  return useMemo(() => {
    return { saving: savingProject && storeSaveId === saveId };
  }, [savingProject, saveId, storeSaveId]);
};

export default useOnFinishSave;
